import { forwardRef, useState, useMemo } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Select — searchless yoki searchable dropdown (Faza 8 polish).
 *
 * Combobox dan farqi:
 *   - Search ixtiyoriy (`searchable` prop)
 *   - Multi-select (`multiple` prop)
 *   - Group support (`groups` prop)
 *
 * Token'lar: --form-field-* (Combobox bilan bir xil trigger)
 *
 * A11y: aria-haspopup=listbox, aria-expanded
 */
export interface SelectOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
  /** Optional ikon (Lucide node) */
  icon?: React.ReactNode;
}

export interface SelectGroup {
  label: string;
  options: SelectOption[];
}

interface BaseProps {
  options?: SelectOption[];
  groups?: SelectGroup[];
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyText?: string;
  width?: number;
  className?: string;
  triggerClassName?: string;
  id?: string;
  "aria-label"?: string;
}

interface SingleProps extends BaseProps {
  multiple?: false;
  value?: string | null;
  onValueChange?: (value: string | null) => void;
  clearable?: boolean;
}

interface MultiProps extends BaseProps {
  multiple: true;
  value?: string[];
  onValueChange?: (value: string[]) => void;
  /** Multi-select trigger: tanlangan miqdorni ko'rsatish */
  showCount?: boolean;
}

export type SelectProps = SingleProps | MultiProps;

function flatten(options?: SelectOption[], groups?: SelectGroup[]): SelectOption[] {
  if (groups) return groups.flatMap((g) => g.options);
  return options ?? [];
}

export const Select = forwardRef<HTMLButtonElement, SelectProps>(function Select(props, ref) {
  const {
    options,
    groups,
    placeholder = "Tanlang...",
    disabled,
    invalid,
    searchable = false,
    searchPlaceholder = "Qidirish...",
    emptyText = "Topilmadi",
    width = 280,
    className,
    triggerClassName,
    id,
    "aria-label": ariaLabel,
  } = props;

  const isMulti = props.multiple === true;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const all = useMemo(() => flatten(options, groups), [options, groups]);

  const selectedSingle: SelectOption | null = useMemo(() => {
    if (isMulti) return null;
    const v = (props as SingleProps).value ?? null;
    return all.find((o) => o.value === v) ?? null;
  }, [isMulti, props, all]);

  const selectedMulti: SelectOption[] = useMemo(() => {
    if (!isMulti) return [];
    const arr = (props as MultiProps).value ?? [];
    return all.filter((o) => arr.includes(o.value));
  }, [isMulti, props, all]);

  const filterFn = (opt: SelectOption) => {
    if (!search) return true;
    return (
      opt.label.toLowerCase().includes(search.toLowerCase()) ||
      opt.value.toLowerCase().includes(search.toLowerCase())
    );
  };

  const renderOption = (opt: SelectOption) => {
    const isSelected = isMulti
      ? ((props as MultiProps).value ?? []).includes(opt.value)
      : (props as SingleProps).value === opt.value;
    return (
      <button
        key={opt.value}
        type="button"
        role="option"
        aria-selected={isSelected}
        disabled={opt.disabled}
        onClick={() => {
          if (isMulti) {
            const cur = (props as MultiProps).value ?? [];
            const next = cur.includes(opt.value)
              ? cur.filter((v) => v !== opt.value)
              : [...cur, opt.value];
            (props as MultiProps).onValueChange?.(next);
          } else {
            (props as SingleProps).onValueChange?.(opt.value);
            setOpen(false);
          }
        }}
        className={cn(
          "flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-sm",
          "text-[var(--text-secondary)] hover:bg-[var(--bg-surface)] hover:text-[var(--text-primary)]",
          isSelected && "bg-[var(--bg-surface)] text-[var(--text-primary)]",
          opt.disabled && "cursor-not-allowed opacity-50",
        )}
      >
        <Check
          className={cn(
            "h-4 w-4 mt-0.5 shrink-0 text-[var(--accent-violet)]",
            isSelected ? "opacity-100" : "opacity-0",
          )}
          aria-hidden
        />
        {opt.icon && <span className="mt-0.5 shrink-0">{opt.icon}</span>}
        <span className="flex-1 min-w-0">
          <span className="block truncate">{opt.label}</span>
          {opt.description && (
            <span className="block text-[11px] text-[var(--text-muted)] truncate">
              {opt.description}
            </span>
          )}
        </span>
      </button>
    );
  };

  const triggerLabel = (() => {
    if (isMulti) {
      const len = selectedMulti.length;
      if (len === 0) return placeholder;
      if ((props as MultiProps).showCount && len > 1) return `${len} ta tanlangan`;
      return selectedMulti.map((o) => o.label).join(", ");
    }
    return selectedSingle?.label ?? placeholder;
  })();

  const isEmptyValue = isMulti ? selectedMulti.length === 0 : !selectedSingle;
  const showClear =
    !isMulti && (props as SingleProps).clearable && selectedSingle && !disabled;

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={(v) => !disabled && setOpen(v)}>
      <PopoverPrimitive.Trigger asChild>
        <button
          ref={ref}
          id={id}
          type="button"
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-haspopup="listbox"
          disabled={disabled}
          className={cn(
            "inline-flex h-10 w-full items-center justify-between gap-2 rounded-lg border px-3 text-sm",
            "bg-[var(--form-field-bg)] border-[var(--form-field-border)] text-[var(--text-primary)]",
            "transition-colors duration-150",
            "hover:border-[var(--form-field-border-hover)]",
            "focus:border-[var(--form-field-border-focus)] focus:outline-none focus:ring-2 focus:ring-[var(--form-field-ring-focus)]",
            "disabled:cursor-not-allowed disabled:opacity-50",
            invalid &&
              "border-[var(--form-error-border)] focus:border-[var(--form-error-border-focus)] focus:ring-[var(--form-error-ring)]",
            triggerClassName,
            className,
          )}
        >
          <span className={cn("truncate", isEmptyValue && "text-[var(--form-field-placeholder)]")}>
            {triggerLabel}
          </span>
          <span className="flex items-center gap-1 shrink-0">
            {showClear && (
              <span
                role="button"
                aria-label="Tozalash"
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation();
                  (props as SingleProps).onValueChange?.(null);
                }}
                className="inline-flex h-5 w-5 items-center justify-center rounded hover:bg-[var(--bg-surface)]"
              >
                <X className="h-3 w-3 text-[var(--text-muted)]" />
              </span>
            )}
            <ChevronDown
              className={cn(
                "h-4 w-4 text-[var(--text-muted)] transition-transform duration-150",
                open && "rotate-180",
              )}
              aria-hidden
            />
          </span>
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          className={cn(
            "z-[var(--z-popover,60)] rounded-lg border border-[var(--border-strong)] bg-[var(--bg-elevated)]",
            "shadow-[var(--shadow-modal)] overflow-hidden",
            "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
            "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
          )}
          style={{ width }}
        >
          {searchable && (
            <div className="border-b border-[var(--border-subtle)] px-2 py-1.5">
              <div className="relative">
                <Search
                  aria-hidden
                  className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-muted)]"
                />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="h-8 w-full rounded bg-transparent pl-7 pr-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] outline-none"
                />
              </div>
            </div>
          )}
          <div role="listbox" className="max-h-[260px] overflow-y-auto p-1">
            {groups
              ? groups.map((g) => {
                  const filtered = g.options.filter(filterFn);
                  if (filtered.length === 0) return null;
                  return (
                    <div key={g.label} className="mb-1 last:mb-0">
                      <div className="px-2 py-1 text-[10px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
                        {g.label}
                      </div>
                      <div className="flex flex-col gap-0.5">
                        {filtered.map(renderOption)}
                      </div>
                    </div>
                  );
                })
              : (options ?? []).filter(filterFn).map(renderOption)}
            {(() => {
              const total = groups
                ? groups.reduce((acc, g) => acc + g.options.filter(filterFn).length, 0)
                : (options ?? []).filter(filterFn).length;
              if (total === 0) {
                return (
                  <div className="py-6 text-center text-sm text-[var(--text-muted)]">
                    {emptyText}
                  </div>
                );
              }
              return null;
            })()}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}) as (p: SelectProps & { ref?: React.Ref<HTMLButtonElement> }) => React.ReactElement;
